import React, { useRef, useEffect, useCallback } from 'react';
import type { NodeInspectorProps } from './types';
import { ArrowDownLeft, ArrowUpRight, Clock, Copy, ChevronDown, ChevronUp, Plus, Search, Eye, EyeOff, ShieldCheck, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { SkeletonList } from '../ui/Skeleton';
import { useSelectionStore } from '../../stores/useSelectionStore';
import { useGraphStore } from '../../stores/useGraphStore';
import { useStashStore } from '../../stores/useStashStore';
import { canvasController } from '../../services/canvasController';
import { useTransactionStore } from '../../stores/useTransactionStore';
import { useFilterStore } from '../../stores/useFilterStore';
import { safeStr } from '../../utils/safeStr';
import { displayEntityName } from '../../utils/displayName';
import { formatCurrency, getDisplayMagnitude, isDustValue, isUnknownValue, txRowUsd, DUST_USD_THRESHOLD, getEdgeSelectionTxs } from '../../utils/edgeConsolidation';
import { stampKind, stampBadgeText, summarizeStamped, claimedTotalText, isHiddenDustRow } from '../../utils/stampedRows';
import { recordInspectorData } from '../../utils/pipelineRecorder';
import { normalizeCanvasAddr } from '../../utils/canvasSync';
import { EntityNameWithCopy } from './EntityNameWithCopy';
import { reportClientError } from '../../utils/reportClientError';
import clsx from 'clsx';
import { loadedSummary, partialReason, NEXT_TX_BATCH } from '../../utils/txCoverage';

const truncateAddr = (addr: string) => {
    if (!addr) return '—';
    return addr.length > 12 ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : addr;
};

// Error boundary to catch #185 and log the exact problematic data
class TxErrorBoundary extends React.Component<{ children: React.ReactNode; txs: any[] }, { error: Error | null }> {
    state: { error: Error | null } = { error: null };
    static getDerivedStateFromError(error: Error) { return { error }; }
    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error('[TxErrorBoundary] Caught render error in transaction list:', error, errorInfo);
        reportClientError(error, { surface: 'transfers', endpoint: 'TxErrorBoundary' });
    }
    render() {
        if (this.state.error) {
            return (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-400">
                    <p className="font-bold mb-1">Transaction render error</p>
                    <p className="font-mono text-[10px] break-all">{this.state.error.message}</p>
                    <button onClick={() => this.setState({ error: null })} className="mt-2 px-2 py-1 bg-red-500/20 rounded hover:bg-red-500/30">Retry</button>
                </div>
            );
        }
        return this.props.children;
    }
}

// Helper to handle both ms and sec timestamps safely
const toSafeDate = (ts: any): Date | null => {
    if (!ts) return null;
    const num = Number(ts);
    if (!isNaN(num)) {
        return new Date(num > 10000000000 ? num : num * 1000);
    }
    const d = new Date(String(ts));
    return isNaN(d.getTime()) ? null : d;
};

type SortField = 'date' | 'amount';
type SortDir = 'asc' | 'desc';

export const TransfersTab: React.FC<NodeInspectorProps> = (props) => {
    const {
        selectedNode,
        chain,
        txFilter = { direction: 'all', token: 'all' },
        selectedEdgeTxHash,
        toggleTxSelection,
        txStartDate = '',
        txEndDate = '',
        onLoadMore,
        loadingMore,
        dateRangeAllLoaded,
        selectedEdgePair,
        edgeTxMap,
        canvasTxHashes,
        onAddCounterpartyToCanvas,
        onRemoveFromCanvas,
        autoExpandTxHash,
        onExpandNode,
    } = props;

    // Subscribe to currencyMode so visibleMax + per-row gauges + formatted values
    // all re-render when the inspector header ORIG/USD toggle flips.
    useFilterStore(s => s.currencyMode);
    // Dust toggle (default ON). When ON, sub-cent transactions are dropped
    // from the list. When OFF, they're rendered but deprioritised — see
    // TransactionCard's isDust prop wiring below.
    const hideDust = useFilterStore(s => s.hideDust);
    const setHideDust = useFilterStore(s => s.setHideDust);

    const [sortField, setSortField] = React.useState<SortField>('date');
    const [sortDir, setSortDir] = React.useState<SortDir>('desc');
    const [counterpartySearch, setCounterpartySearch] = React.useState('');
    const [amountMin, setAmountMin] = React.useState('');
    // Which big tx_hash groups (starbursts) the user has expanded inline.
    const [expandedTxHashes, setExpandedTxHashes] = React.useState<Set<string>>(() => new Set());
    const toggleTxHashGroup = useCallback((h: string) => {
        setExpandedTxHashes(prev => {
            const next = new Set(prev);
            if (next.has(h)) next.delete(h); else next.add(h);
            return next;
        });
    }, []);
    // "Show" on the N-hidden line reveals the dust rows for THIS node only (no global toggle change).
    const [revealDust, setRevealDust] = React.useState(false);
    // Reset expanded groups when the inspected node changes.
    useEffect(() => {
        setExpandedTxHashes(new Set());
        setRevealDust(false);
    }, [selectedNode?.id]);
    // One-shot auto-expand from parent (cluster left-click / "View in Transfers"
    // right-click). Track last-consumed value so a re-fire with the same hash
    // is a no-op and doesn't fight the user collapsing it.
    const lastAutoExpandRef = useRef<string | null>(null);
    useEffect(() => {
        if (!autoExpandTxHash) return;
        if (lastAutoExpandRef.current === autoExpandTxHash) return;
        lastAutoExpandRef.current = autoExpandTxHash;
        setExpandedTxHashes(prev => {
            if (prev.has(autoExpandTxHash)) return prev;
            const next = new Set(prev);
            next.add(autoExpandTxHash);
            return next;
        });
    }, [autoExpandTxHash]);

    const counterpartyFilter = useSelectionStore(s => s.counterpartyFilter);
    const hoveredNodeId = useSelectionStore(s => s.hoveredNodeId);
    const hoveredSource = useSelectionStore(s => s.hoveredSource);
    const setHoveredNode = useSelectionStore(s => s.setHoveredNode);

    const selectedTxRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (selectedEdgeTxHash && selectedTxRef.current) {
            setTimeout(() => {
                selectedTxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }, 100);
        }
    }, [selectedEdgeTxHash]);

    const handleRowHover = useCallback((counterpartyAddr: string | null) => {
        setHoveredNode(counterpartyAddr, 'inspector');
    }, [setHoveredNode]);

    const graphElements = useGraphStore(s => s.elements);
    // A5: subscribe to the stash store so the per-row eye toggle AND the "Show
    // all" reset bar stay in sync when a node is stashed/restored from ANY
    // surface (canvas right-click, Inspector, Stash Tray) — not just this tab.
    // Before this the eye read useStashStore.getState() at render time, so it
    // went stale on an external stash change. Subscribing here re-renders the
    // tab (and the cards) on every stash mutation. hiddenCanvasAddresses also
    // backs the reset bar below (mirrors CounterpartiesTab).
    const stashedEntries = useStashStore(s => s.entries);
    const hiddenCanvasAddresses = React.useMemo(() => {
        const addrs = new Set<string>();
        for (const key in stashedEntries) {
            const entry = stashedEntries[key];
            if (entry.group !== 'nodes') continue;
            const id = entry.element?.data?.id;
            if (!id) continue;
            addrs.add(id);
            if (id.startsWith('0x') || id.startsWith('0X')) addrs.add(id.toLowerCase());
        }
        return addrs;
    }, [stashedEntries]);
    const traceTxHashes = React.useMemo(() => {
        const hashes = new Set<string>();
        if (!selectedNode?.id) return hashes;
        for (const el of graphElements) {
            if (el.data?.edgeType === 'gold-path' &&
                (el.data.source === selectedNode.id || el.data.target === selectedNode.id)) {
                if (el.data.tx_hash) hashes.add(el.data.tx_hash);
                if (Array.isArray(el.data.trace_transactions)) {
                    for (const t of el.data.trace_transactions) {
                        if (t.tx_hash) hashes.add(t.tx_hash);
                    }
                }
            }
        }
        return hashes;
    }, [graphElements, selectedNode?.id]);

    const storeTxs = useTransactionStore(s => s.getNodeTransactions(selectedNode?.id || ''));

    // Synthetic tx rows derived from canvas edges that the on-chain fetch may
    // never produce: forensic-overlay edges (subpoena-attested transfers) and
    // members of starburst clusters that converge on the selected node. The
    // Transfers tab is the user's "show me everything" surface — without this,
    // a cluster of 228 co-spenders is invisible until the user expands the
    // cluster on canvas, and that mutates the layout. This keeps the canvas
    // bundled while still letting the user see + group every underlying row.
    const canvasEdgeTxs = React.useMemo(() => {
        if (!selectedNode?.id) return [] as any[];
        const sid = selectedNode.id;
        const out: any[] = [];

        const edgeToTx = (edge: any): any => {
            const src = String(edge.source || '');
            const tgt = String(edge.target || '');
            const isIncoming = tgt === sid;
            const counterparty = isIncoming ? src : tgt;
            const amountRaw = edge.amount;
            const amount =
                typeof amountRaw === 'number'
                    ? amountRaw
                    : amountRaw != null
                        ? parseFloat(String(amountRaw))
                        : 0;
            return {
                tx_hash: edge.tx_hash || `forensic:${edge.forensic_record_id || edge.id || ''}`,
                from_address: src,
                to_address: tgt,
                counterparty,
                value: isFinite(amount) ? amount : 0,
                token_symbol: edge.currency || '',
                timestamp: edge.timestamp,
                direction: isIncoming ? 'incoming' : 'outgoing',
                forensic_context: {
                    verified: edge.forensic_edge === 'proof' || edge.forensic_edge === 'verified',
                    forensic_edge: edge.forensic_edge,
                    forensic_record_id: edge.forensic_record_id,
                    link_type: edge.forensic_edge,
                    exchange_internal_id: edge.exchange_internal_id,
                },
                _synthetic_source: 'canvas_forensic_edge',
            };
        };

        for (const el of graphElements) {
            const d = el?.data;
            if (!d?.source || !d?.target) continue;
            // Direct forensic edge attached to the selected node
            if ((d.source === sid || d.target === sid) && (d.forensic_record_id || d.forensic_edge)) {
                out.push(edgeToTx(d));
            }

            // B3 fix (May 4 2026 audit): pull trace_transactions[] from
            // gold-path / auto-trace edges that touch the selected node.
            // The trace algorithm reports edges with tx_count = N but only
            // injects the primary representative tx into expanded_transactions
            // on the endpoint nodes; the remaining (N-1) txs live ONLY on
            // the edge's trace_transactions array. Without this merge the
            // node's Transfers tab silently shows fewer rows than the edge
            // label claims (the 306,304 USDT × 3 vs "2 txs of 145K" gap
            // the user reported). De-duplication happens in baseTxs by
            // (tx_hash, from, to) triple, so re-pushing the representative
            // is a no-op when it's already present.
            if ((d.source === sid || d.target === sid) && Array.isArray(d.trace_transactions) && d.trace_transactions.length > 0) {
                const isIncoming = d.target === sid;
                const counterparty = isIncoming ? d.source : d.target;
                for (const t of d.trace_transactions) {
                    out.push({
                        tx_hash: t.tx_hash || d.tx_hash,
                        from_address: d.source,
                        to_address: d.target,
                        counterparty,
                        value: t.value ?? d.value ?? 0,
                        timestamp: t.timestamp || d.timestamp || null,
                        token_symbol: t.token_symbol || d.token_symbol || null,
                        block_height: t.block_height,
                        direction: isIncoming ? 'incoming' : 'outgoing',
                        _synthetic_source: 'gold_path_trace_tx',
                    });
                }
            }
        }

        // Bundled cluster members converging on the selected node — each bundled
        // row becomes a synthetic tx so the user can browse the 228 co-spenders
        // in Transfers without expanding the cluster on canvas.
        for (const el of graphElements) {
            const d = el?.data;
            if (!d || d.source) continue; // nodes only
            if (d.type !== 'cluster') continue;
            if (d.convergence_node_id !== sid) continue;
            const bundled = Array.isArray(d.bundled_edges) ? d.bundled_edges : [];
            for (const be of bundled) out.push(edgeToTx(be));
        }

        // E2E diagnostic: stash counts on window so Playwright tests can
        // verify the synthetic-tx pipeline without reaching into React state.
        // Cheap (one window write per memo) and removable later.
        return out;
    }, [graphElements, selectedNode?.id]);

    // address-lowered → entity_name lookup from canvas. Used to backfill
    // tx.from_entity_name / tx.to_entity_name on edge-derived rows that
    // weren't enriched at fetch time. Without this, selecting an edge
    // between two named exchanges showed both sides as truncated addresses
    // ("0xa9d1e0…3e43 → 0x07ab8c…106b") because the synthetic edge txs
    // only carry the relative `counterparty_entity` field, which is
    // resolved against `selectedNodeId` and goes blank when neither edge
    // endpoint is the selected node. Looking up from the canvas store
    // here gives us the entity name regardless of selection state.
    const addrNameMap = React.useMemo(() => {
        const m = new Map<string, string>();
        for (const el of graphElements) {
            if (el.data?.source) continue; // edges
            const id = el.data?.id;
            const name = el.data?.entity_name;
            if (id && name && name !== 'Unknown') {
                m.set(String(id).toLowerCase(), String(name));
            }
        }
        return m;
    }, [graphElements]);

    const baseTxs = React.useMemo(() => {
        let txs: any[] = [];
        if (selectedEdgePair) {
            // Edge selection: assemble real per-tx data from real sources only,
            // via the shared 3-layer cascade (A6). NEVER fabricates a row from
            // edge metadata — if no real source has data the Transfers panel
            // falls through to its honest empty state. Full doc + the no-
            // synthetic-row rationale live on getEdgeSelectionTxs.
            txs = getEdgeSelectionTxs({
                edgePair: selectedEdgePair,
                edgeTxMap,
                graphElements,
                nodeTxs: storeTxs.length > 0 ? storeTxs : (selectedNode.expanded_transactions || []),
            });
        } else if (txs.length === 0) {
            // No edge selected — show the selected node's tx list as before.
            txs = storeTxs.length > 0 ? storeTxs : (selectedNode.expanded_transactions || []);
        }

        // Merge canvas-edge-derived synthetic rows. Dedup key is the
        // (tx_hash, from, to) triple — the actual identity of a transfer.
        //
        // Why not forensic_record_id: the forensic-overlay consolidator
        // emits aggregated edges where every aggregated edge from a single
        // multi-row subpoena entry shares the SAME record_id (it carries
        // first.record_id). Using record_id collapses all 228 co-spender
        // synth rows into 1.
        //
        // Why not tx_hash alone: a starburst has 228 rows all sharing one
        // tx_hash. We want to KEEP all 228, only dropping a synth that
        // duplicates an API row at the (hash, from, to) identity level.
        //
        // Clone first — txs may alias the tx store array, and pushing into
        // it would mutate global state.
        if (canvasEdgeTxs.length > 0) {
            const tripleKey = (t: any) =>
                `${String(t.tx_hash || '')}|${String(t.from_address || '')}|${String(t.to_address || '')}`;
            const seen = new Set<string>();
            for (const t of txs) {
                if (t.tx_hash) seen.add(tripleKey(t));
            }
            const merged = txs.slice();
            for (const synth of canvasEdgeTxs) {
                if (!synth.tx_hash) { merged.push(synth); continue; }
                const k = tripleKey(synth);
                if (seen.has(k)) continue;
                merged.push(synth);
                seen.add(k);
            }
            txs = merged;
        }

        // Backfill from_entity_name / to_entity_name from canvas when the
        // tx itself doesn't carry them. Critical for edge-selection rows
        // whose synthetic txs only have addresses, not entity names.
        // No-op when the row already has the names.
        if (addrNameMap.size > 0) {
            txs = txs.map(t => {
                const fromName = t.from_entity_name || addrNameMap.get(String(t.from_address || '').toLowerCase());
                const toName = t.to_entity_name || addrNameMap.get(String(t.to_address || '').toLowerCase());
                if (fromName === t.from_entity_name && toName === t.to_entity_name) return t;
                return { ...t, from_entity_name: fromName, to_entity_name: toName };
            });
        }

        return txs;
    }, [storeTxs, selectedNode.expanded_transactions, selectedEdgePair, edgeTxMap, canvasEdgeTxs, addrNameMap, graphElements]);

    // WB07 (NS-38): what the dust rule hides and what the ingest stamps took out of the totals.
    const hiddenSummary = React.useMemo(() => {
        const stamped = summarizeStamped(baseTxs);
        const dust = baseTxs.reduce((n: number, t: any) => n + (isHiddenDustRow(t) ? 1 : 0), 0);
        return { ...stamped, dust };
    }, [baseTxs]);

    // Detect if the selected edge is a "common-attribution" link.  These
    // edges have no transactions (they represent shared entity ownership,
    // not a direct on-chain transfer) — so we show an explanatory panel
    // instead of the empty transaction list.
    const attributionEdge = React.useMemo(() => {
        if (!selectedEdgePair) return null;
        const match = graphElements.find(el =>
            el.data?.edgeType === 'common-attribution' &&
            ((el.data.source === selectedEdgePair.source && el.data.target === selectedEdgePair.target) ||
             (el.data.source === selectedEdgePair.target && el.data.target === selectedEdgePair.source))
        );
        return match?.data || null;
    }, [selectedEdgePair, graphElements]);

    // Canvas edge for the current selection — used to detect SSE-discovered
    // edges that carry aggregate value/count but no per-tx payload yet.
    const selectedCanvasEdge = React.useMemo(() => {
        if (!selectedEdgePair) return null;
        return graphElements.find(el =>
            el.data?.source && el.data?.target &&
            ((el.data.source === selectedEdgePair.source && el.data.target === selectedEdgePair.target) ||
             (el.data.source === selectedEdgePair.target && el.data.target === selectedEdgePair.source))
        ) || null;
    }, [selectedEdgePair, graphElements]);

    const isSseSparseSelection = React.useMemo(() => {
        if (baseTxs.length > 0) return false;
        if (selectedCanvasEdge) {
            const d = selectedCanvasEdge.data || {};
            const classes = String(selectedCanvasEdge.classes || '');
            const discovering = classes.includes('discovering') || d._visibility === 0.55;
            const hasAggregate = !!(d.tx_count || d.value || d.label || d.edge_type || d.edgeType);
            const viaStep = !!(d.step || d.discovered_via || d.discovery_step);
            return discovering || hasAggregate || viaStep;
        }
        // Node with no txs — SSE discovery often lands without expanded_transactions.
        const classes = String(
            graphElements.find(el => !el.data?.source && el.data?.id === selectedNode?.id)?.classes || ''
        );
        return classes.includes('discovering')
            || !!(selectedNode as any)?.discovered_via
            || !!(selectedNode as any)?.step;
    }, [baseTxs.length, selectedCanvasEdge, graphElements, selectedNode]);

    // Stale-response guard for the empty-state Load / Expand action (R8).
    const loadRequestGenRef = useRef(0);
    useEffect(() => {
        loadRequestGenRef.current += 1;
    }, [selectedNode?.id, selectedEdgePair?.source, selectedEdgePair?.target]);

    const handleEmptyStateLoad = useCallback(async () => {
        const gen = ++loadRequestGenRef.current;
        const expand = onExpandNode || ((node?: any) => onLoadMore?.());
        try {
            if (selectedEdgePair) {
                // Prefer expanding the currently inspected node when it is an
                // endpoint; otherwise expand both endpoints so Transfers fills.
                const endpoints = [selectedEdgePair.source, selectedEdgePair.target];
                const preferred = endpoints.includes(selectedNode?.id)
                    ? [selectedNode!.id]
                    : endpoints;
                for (const addr of preferred) {
                    if (gen !== loadRequestGenRef.current) return; // stale
                    await Promise.resolve(expand({ id: addr }));
                }
            } else if (selectedNode?.id) {
                await Promise.resolve(expand(selectedNode));
            } else {
                onLoadMore?.();
            }
        } finally {
            // Ignore late completions after node/edge changed mid-flight.
            if (gen !== loadRequestGenRef.current) return;
        }
    }, [onExpandNode, onLoadMore, selectedEdgePair, selectedNode]);

    const handleToggleSelect = (tx: any) => {
        toggleTxSelection?.(tx);
    };

    // Render a dedicated panel for common-attribution edges — these have no
    // transactions so the normal tx list would be confusing.
    if (attributionEdge) {
        const entityName = attributionEdge.attribution_entity_name || attributionEdge.label || 'Shared entity';
        const category = attributionEdge.attribution_entity_category || '';
        const memberCount = attributionEdge.attribution_member_count || 2;
        return (
            <div className="flex flex-col p-3" data-testid="attribution-panel">
                <div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="inline-block w-2 h-2 rounded-full bg-primary" />
                        <span className="text-xs font-semibold text-pink-400 uppercase tracking-wide">Common attribution</span>
                    </div>
                    <div className="text-sm text-foreground font-semibold mb-1">{entityName}</div>
                    {category && (
                        <div className="text-[11px] text-muted-foreground mb-3 capitalize">Category: {category}</div>
                    )}
                    <p className="text-[12px] text-muted-foreground leading-relaxed mb-3">
                        This is a <strong className="text-pink-400">speculative link</strong>, not an on-chain transaction.
                        {' '}{memberCount} address{memberCount !== 1 ? 'es' : ''} on this canvas share the same entity
                        attribution (<span className="text-foreground">{entityName}</span>), which typically means they are
                        all deposit / withdrawal wallets controlled by the same operator.
                    </p>
                    <p className="text-[11px] text-muted-foreground/80 leading-relaxed">
                        No direct transaction exists between these addresses. Funds typically move through the
                        operator&apos;s internal infrastructure (hot wallets, omnibus accounts) which is not visible on-chain.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col" data-testid="transfers-tab">
            {/* Filters Bar — participates in the inspector body scroll (no nested
                list viewport). Pinning filters used to leave only a tiny tx list. */}
            <div className="flex flex-wrap items-center gap-2 mb-2">
                <div className="relative flex-1 min-w-[120px]">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                    <input
                        type="text"
                        placeholder="Search parties..."
                        value={counterpartySearch}
                        onChange={(e) => setCounterpartySearch(e.target.value)}
                        className="w-full pl-7 pr-2 py-1 bg-secondary/30 border border-border/50 rounded text-[11px] focus:outline-none focus:ring-1 focus:ring-primary/30"
                    />
                </div>
                <div className="flex items-center gap-1 bg-secondary/30 border border-border/50 rounded p-0.5">
                    <button
                        onClick={() => { setSortField('date'); setSortDir(prev => prev === 'desc' ? 'asc' : 'desc'); }}
                        className={`px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors ${sortField === 'date' ? 'bg-background text-primary shadow-sm' : 'text-muted-foreground'}`}
                        title="Sort by on-chain block time (confirmation timestamp), not when the row was loaded"
                    >
                        Date {sortField === 'date' && (sortDir === 'desc' ? '↓' : '↑')}
                    </button>
                    <button
                        onClick={() => { setSortField('amount'); setSortDir(prev => prev === 'desc' ? 'asc' : 'desc'); }}
                        className={`px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors ${sortField === 'amount' ? 'bg-background text-primary shadow-sm' : 'text-muted-foreground'}`}
                    >
                        Amount {sortField === 'amount' && (sortDir === 'desc' ? '↓' : '↑')}
                    </button>
                </div>
                {/* Dust toggle — pill-style. Mirrors the one in TxLoadBar
                  * (Counterparties tab) so users can flip it from either tab. */}
                <button
                    onClick={() => setHideDust(!hideDust)}
                    className={`shrink-0 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border transition-colors ${
                        hideDust
                            ? 'border-primary/50 bg-primary/10 text-primary'
                            : 'border-input text-muted-foreground hover:text-foreground'
                    }`}
                    title={
                        hideDust
                            ? `Hiding dust (<$${DUST_USD_THRESHOLD}) — click to show with reduced weight`
                            : `Showing dust (<$${DUST_USD_THRESHOLD}) deprioritised — click to hide`
                    }
                >
                    <Filter className="w-3 h-3" />
                    <span>{hideDust ? 'Hide dust' : 'Show dust'}</span>
                </button>
            </div>

            {/* How much of the history is loaded: "N of M" only with a real M, else "N loaded, more available" /
              * "N loaded, complete" from the fetcher's has_more (NS-36). */}
            {!selectedEdgePair && baseTxs.length > 0 && (
                <div className="flex items-center gap-1.5 mb-1 text-[10px] text-muted-foreground" data-testid="transfers-coverage">
                    <span data-testid="transfers-coverage-summary">
                        {loadedSummary(baseTxs.length, selectedNode?.tx_total_real ?? selectedNode?.tx_count, selectedNode?.tx_has_more)}
                    </span>
                    {(selectedNode?.tx_truncated || selectedNode?.tx_incomplete) && (
                        <span
                            data-testid="transfers-partial-chip"
                            title={partialReason({ truncated: selectedNode?.tx_truncated, incomplete: selectedNode?.tx_incomplete })}
                            className="px-1 rounded bg-amber-500/15 text-amber-600 dark:text-amber-400"
                        >
                            partial
                        </span>
                    )}
                </div>
            )}

            {/* "N hidden" line (NS-38): dust the rule hid (one click reveals it for this node) and stamped
              * counterfeit / look-alike rows, which stay in the list but are excluded from the totals. */}
            {!selectedEdgePair && (hiddenSummary.dust > 0 || hiddenSummary.counterfeit > 0 || hiddenSummary.poisoning > 0) && (
                <div className="flex flex-wrap items-center gap-1.5 mb-1 text-[10px] text-muted-foreground" data-testid="transfers-hidden-line">
                    {hiddenSummary.dust > 0 && (
                        <span data-testid="transfers-hidden-dust">
                            {hideDust && !revealDust ? `${hiddenSummary.dust} hidden as dust` : `${hiddenSummary.dust} dust rows shown`}
                            {hideDust && (
                                <button
                                    type="button"
                                    data-testid="transfers-hidden-toggle"
                                    onClick={() => setRevealDust(v => !v)}
                                    className="ml-1 underline hover:text-foreground"
                                >
                                    {revealDust ? 'hide' : 'show'}
                                </button>
                            )}
                        </span>
                    )}
                    {hiddenSummary.counterfeit > 0 && (
                        <span data-testid="transfers-hidden-counterfeit">
                            {hiddenSummary.counterfeit} counterfeit-token row{hiddenSummary.counterfeit === 1 ? '' : 's'}
                            {Object.keys(hiddenSummary.claimed).length > 0 ? ` (claimed ${claimedTotalText(hiddenSummary.claimed)})` : ''}
                        </span>
                    )}
                    {hiddenSummary.poisoning > 0 && (
                        <span data-testid="transfers-hidden-poisoning">
                            {hiddenSummary.poisoning} look-alike-sender row{hiddenSummary.poisoning === 1 ? '' : 's'}
                        </span>
                    )}
                    {(hiddenSummary.counterfeit > 0 || hiddenSummary.poisoning > 0) && (
                        <span data-testid="transfers-excluded-note">excluded from totals</span>
                    )}
                </div>
            )}

            {/* Transaction List — natural height; inspector body owns scrolling. */}
            <TxErrorBoundary txs={baseTxs}>
                <div className="pr-1">
                    {/* Privacy chain TX node — show TX metadata since sender/receiver are hidden */}
                    {baseTxs.length === 0 && (selectedNode as any)?.is_privacy_chain && (selectedNode as any)?.type === 'transaction' && (
                        <div className="space-y-3 mb-3">
                            <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="text-primary text-xs font-semibold">Privacy Chain Transaction</span>
                                    <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${
                                        (selectedNode as any).privacy_chain_tier === 1 ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'
                                    }`}>
                                        TIER {(selectedNode as any).privacy_chain_tier}
                                    </span>
                                    {(selectedNode as any).protocol_era && (
                                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20">
                                            {(selectedNode as any).protocol_era}
                                        </span>
                                    )}
                                </div>
                                {/* Core metadata as a transaction row */}
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[11px]">
                                    <div><span className="text-muted-foreground">Hash:</span> <span className="font-mono text-foreground">{((selectedNode as any).tx_hash || selectedNode.id)?.slice(0, 20)}...</span></div>
                                    <div><span className="text-muted-foreground">Block:</span> <span className="font-mono text-foreground">{(selectedNode as any).block_height?.toLocaleString() ?? '—'}</span></div>
                                    <div><span className="text-muted-foreground">Time:</span> <span className="text-foreground">{(selectedNode as any).timestamp ? new Date(String((selectedNode as any).timestamp)).toLocaleString('en-IN', {dateStyle:'medium', timeStyle:'short'}) : '—'}</span></div>
                                    <div><span className="text-muted-foreground">Fee:</span> <span className="font-mono text-foreground">{(selectedNode as any).fee != null ? `${Number((selectedNode as any).fee).toFixed(8)} ${selectedNode.chain}` : '—'}</span></div>
                                    {(selectedNode as any).ring_size != null && (
                                        <div><span className="text-muted-foreground">Ring Size:</span> <span className="font-mono text-foreground">{(selectedNode as any).ring_size}{(selectedNode as any).ring_size >= 16 ? ' (max)' : ''}</span></div>
                                    )}
                                    <div><span className="text-muted-foreground">Inputs/Outputs:</span> <span className="font-mono text-foreground">{(selectedNode as any).num_inputs ?? '?'} / {(selectedNode as any).num_outputs ?? '?'}</span></div>
                                    {(selectedNode as any).tx_size_bytes != null && (
                                        <div><span className="text-muted-foreground">Size:</span> <span className="font-mono text-foreground">{(selectedNode as any).tx_size_bytes?.toLocaleString()} bytes</span></div>
                                    )}
                                    {(selectedNode as any).confirmations != null && (
                                        <div><span className="text-muted-foreground">Confirmations:</span> <span className="font-mono text-foreground">{(selectedNode as any).confirmations?.toLocaleString()}</span></div>
                                    )}
                                    <div><span className="text-muted-foreground">From:</span> <span className="text-red-400 italic">Hidden</span></div>
                                    <div><span className="text-muted-foreground">To:</span> <span className="text-red-400 italic">Hidden</span></div>
                                    <div><span className="text-muted-foreground">Amount:</span> <span className="text-red-400 italic">Hidden</span></div>
                                    {(selectedNode as any).data_source && (
                                        <div><span className="text-muted-foreground">Source:</span> <span className="text-foreground">{(selectedNode as any).data_source}</span></div>
                                    )}
                                </div>
                            </div>

                            {/* Payment ID — strong forensic signal for exchange deposit attribution */}
                            {(selectedNode as any).payment_id && (
                                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                                    <div className="flex items-center justify-between mb-1.5">
                                        <span className="text-amber-400 text-[10px] font-semibold uppercase tracking-wider">Payment ID — Exchange Deposit Indicator</span>
                                        <button
                                            className="text-[9px] text-muted-foreground hover:text-amber-400 px-1.5 py-0.5 rounded border border-border hover:border-amber-500/40 transition-colors"
                                            onClick={() => navigator.clipboard.writeText((selectedNode as any).payment_id)}
                                            title="Copy payment ID"
                                        >Copy</button>
                                    </div>
                                    <p className="font-mono text-amber-300 text-[11px] break-all mb-1.5">
                                        {(selectedNode as any).payment_id}
                                        {(selectedNode as any).payment_id?.length === 16 && (
                                            <span className="ml-2 text-[9px] text-amber-500/70 font-sans">8-byte integrated</span>
                                        )}
                                        {(selectedNode as any).payment_id?.length === 64 && (
                                            <span className="ml-2 text-[9px] text-amber-500/70 font-sans">32-byte legacy</span>
                                        )}
                                    </p>
                                    <p className="text-[9px] text-amber-400/70 italic leading-relaxed">
                                        8-byte payment IDs are used by exchanges to attribute deposits to user accounts.
                                        This ID is the depositor's account reference at the receiving exchange.
                                        A legal notice to the exchange with this payment ID + timestamp can identify the account holder.
                                    </p>
                                </div>
                            )}

                            {/* Key Images */}
                            {Array.isArray((selectedNode as any).key_images) && (selectedNode as any).key_images.length > 0 && (
                                <div className="bg-secondary/30 border border-border rounded-lg p-3">
                                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5">Key Images ({(selectedNode as any).key_images.length})</p>
                                    <div className="space-y-1">
                                        {((selectedNode as any).key_images as string[]).map((ki: string, i: number) => (
                                            <div key={i} className="flex items-center gap-1.5">
                                                <span className="text-[9px] text-muted-foreground w-4">{i+1}.</span>
                                                <span className="text-[10px] font-mono text-foreground truncate cursor-pointer hover:text-primary"
                                                      onClick={() => { navigator.clipboard.writeText(ki); }}
                                                      title={`Click to copy: ${ki}`}>
                                                    {ki.slice(0, 24)}...{ki.slice(-8)}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                    <p className="text-[9px] text-muted-foreground mt-1.5 italic">
                                        Key images are unique spend tags. If an exchange provides withdrawal records,
                                        matching key images confirms which output was spent.
                                    </p>
                                </div>
                            )}

                            {/* Ring Members */}
                            {Array.isArray((selectedNode as any).ring_members) && (selectedNode as any).ring_members.length > 0 && (
                                <div className="bg-secondary/30 border border-border rounded-lg p-3">
                                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5">
                                        Ring Members ({(selectedNode as any).ring_members.reduce((sum: number, r: any) => sum + (r.members?.length || 0), 0)} decoys across {(selectedNode as any).ring_members.length} input{(selectedNode as any).ring_members.length > 1 ? 's' : ''})
                                    </p>
                                    {((selectedNode as any).ring_members as any[]).map((ring: any, ri: number) => (
                                        <div key={ri} className="mb-2">
                                            <p className="text-[10px] text-primary mb-1">Input #{ri + 1}</p>
                                            <div className="space-y-0.5 ml-2">
                                                {(ring.members || []).slice(0, 8).map((m: any, mi: number) => (
                                                    <div key={mi} className="flex items-center gap-2 text-[10px]">
                                                        <span className="text-muted-foreground w-8">#{mi+1}</span>
                                                        <span className="font-mono text-foreground truncate cursor-pointer hover:text-primary"
                                                              onClick={() => { if (m.tx_hash) navigator.clipboard.writeText(m.tx_hash); }}
                                                              title={m.tx_hash || ''}>
                                                            {m.tx_hash ? `${m.tx_hash.slice(0, 16)}...` : '—'}
                                                        </span>
                                                        <span className="text-muted-foreground">blk {m.block_no?.toLocaleString()}</span>
                                                    </div>
                                                ))}
                                                {(ring.members || []).length > 8 && (
                                                    <p className="text-[10px] text-muted-foreground">+{ring.members.length - 8} more ring members</p>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                    <p className="text-[9px] text-muted-foreground mt-1.5 italic">
                                        One of these is the real spend; the rest are decoys. Known-output elimination
                                        can reduce this set if exchange/seized wallet data is available.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                    {baseTxs.length > 0 ? (
                        (() => {
                            const filteredTxs = baseTxs
                                .filter((tx: any) => {
                                    if (txFilter.direction !== 'all') {
                                        const txDir = tx.direction || (tx.to_address === selectedNode?.id ? 'incoming' : 'outgoing');
                                        if (txDir !== txFilter.direction) return false;
                                    }
                                    if (txFilter.token !== 'all') {
                                        const txToken = safeStr(tx.token_symbol) || (chain === 'TRX' ? 'TRX' : chain === 'BTC' ? 'BTC' : chain === 'SOL' ? 'SOL' : 'ETH');
                                        if (!txToken.includes(txFilter.token)) return false;
                                    }
                                    if (counterpartyFilter) {
                                        const filterKey = counterpartyFilter.address.toLowerCase();
                                        const cpAddr = (tx.counterparty || '').toLowerCase();
                                        const fromAddr = (tx.from_address || '').toLowerCase();
                                        const toAddr = (tx.to_address || '').toLowerCase();
                                        if (cpAddr !== filterKey && fromAddr !== filterKey && toAddr !== filterKey) return false;
                                    }
                                    if (counterpartySearch) {
                                        const q = counterpartySearch.toLowerCase();
                                        const cpAddr = (tx.counterparty || '').toLowerCase();
                                        const cpEntity = safeStr(tx.counterparty_entity || tx.other_address_entity_name).toLowerCase();
                                        if (!cpAddr.includes(q) && !cpEntity.includes(q) && !safeStr(tx.tx_hash).toLowerCase().includes(q)) return false;
                                    }
                                    if (amountMin) {
                                        const minVal = parseFloat(amountMin);
                                        if (!isNaN(minVal) && Math.abs(tx.value ?? 0) < minVal) return false;
                                    }
                                    // Dust filter — drop sub-cent rows when toggle ON.
                                    // When OFF, dust passes through here and is
                                    // deprioritised at render time via the isDust prop.
                                    if (hideDust && !revealDust && isHiddenDustRow(tx)) return false;
                                    return true;
                                });

                    // Pipeline recording: what data reached the Transfers tab vs what's displayed
                    recordInspectorData(
                        'transfers',
                        selectedNode?.id || '',
                        { count: baseTxs.length, source: storeTxs.length > 0 ? 'txStore' : 'expanded' },
                        { count: filteredTxs.length, filtered: baseTxs.length - filteredTxs.length },
                    );

                            const tsToEpoch = (ts: any): number => {
                                const d = toSafeDate(ts);
                                return d ? d.getTime() : 0;
                            };
                            const sortedTxs = [...filteredTxs].sort((a: any, b: any) => {
                                let aVal: number, bVal: number;
                                if (sortField === 'date') {
                                    aVal = tsToEpoch(a.timestamp);
                                    bVal = tsToEpoch(b.timestamp);
                                } else {
                                    aVal = Math.abs(a.value ?? 0);
                                    bVal = Math.abs(b.value ?? 0);
                                }
                                return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
                            });

                            // Reference value for the per-row volume gauge — largest
                            // *displayed* magnitude in the visible window. Uses
                            // getDisplayMagnitude (currency-aware) so a row's bar reflects
                            // the value the user actually sees. Without this, in USD mode
                            // a 0.5 ETH row (~$1750) and a 0.5 USDT row (~$0.5) would
                            // produce identical bars because both have raw value 0.5.
                            const visibleMax = sortedTxs.reduce(
                                (m, t) => (stampKind(t) ? m : Math.max(m, getDisplayMagnitude(t.value ?? 0, t.token_symbol))),
                                1
                            );

                            // Count duplicates by tx_hash so the selected-edge filter
                            // doesn't wipe an entire starburst (228 rows sharing one
                            // hash) when the user clicks the bundle edge.
                            const dupCounts = new Map<string, number>();
                            for (const t of sortedTxs) {
                                if (t.tx_hash) dupCounts.set(t.tx_hash, (dupCounts.get(t.tx_hash) || 0) + 1);
                            }
                            const selectedTx = selectedEdgeTxHash
                                ? sortedTxs.find((t: any) => t.tx_hash === selectedEdgeTxHash)
                                : null;
                            const selHashIsCluster = !!selectedEdgeTxHash &&
                                (dupCounts.get(selectedEdgeTxHash) || 0) > 1;
                            const remainingTxs = selectedTx
                                ? (selHashIsCluster
                                    ? sortedTxs.filter((t: any) => t !== selectedTx)
                                    : sortedTxs.filter((t: any) => t.tx_hash !== selectedEdgeTxHash))
                                : sortedTxs;

                            const traceTxs = remainingTxs.filter((tx: any) => tx.tx_hash && traceTxHashes.has(tx.tx_hash));
                            const regularTxs = remainingTxs.filter((tx: any) => !tx.tx_hash || !traceTxHashes.has(tx.tx_hash));

                            // Split starburst-sized tx_hash groups out of the
                            // counterparty grouping. N rows sharing one hash AND
                            // converging on the selected node render as one
                            // collapsible "228 co-inputs · tx 0d07f0…" header.
                            const TX_HASH_GROUP_THRESHOLD = 10;
                            const remHashCount = new Map<string, number>();
                            for (const t of regularTxs) {
                                if (t.tx_hash) remHashCount.set(t.tx_hash, (remHashCount.get(t.tx_hash) || 0) + 1);
                            }
                            const bigHashes = new Set<string>();
                            for (const [h, n] of remHashCount) {
                                if (n > TX_HASH_GROUP_THRESHOLD) bigHashes.add(h);
                            }
                            const bigTxHashGroups: { tx_hash: string; txs: any[] }[] = [];
                            const bigGroupTxSet = new Set<any>();
                            for (const h of bigHashes) {
                                const grp = regularTxs.filter((t: any) => t.tx_hash === h);
                                bigTxHashGroups.push({ tx_hash: h, txs: grp });
                                for (const t of grp) bigGroupTxSet.add(t);
                            }
                            const restTxs = regularTxs.filter((t: any) => !bigGroupTxSet.has(t));

                            const groupByCounterparty = (txs: any[]): { cpKey: string; cpName: string; txs: any[] }[] => {
                                const groups = new Map<string, { cpKey: string; cpName: string; txs: any[] }>();
                                const order: string[] = [];
                                for (const tx of txs) {
                                    const cp = tx.counterparty || (tx.from_address === selectedNode.id ? tx.to_address : tx.from_address) || '';
                                    const cpName = displayEntityName(cp, tx.counterparty_entity || tx.other_address_entity_name);
                                    if (!groups.has(cp)) {
                                        groups.set(cp, { cpKey: cp, cpName, txs: [] });
                                        order.push(cp);
                                    }
                                    groups.get(cp)!.txs.push(tx);
                                }
                                return order.map(k => groups.get(k)!).filter(Boolean);
                            };
                            const cpGroups = groupByCounterparty(restTxs);

                            return (
                                <div className="space-y-2" onMouseLeave={() => handleRowHover(null)}>
                                    {/* A5: hidden-on-canvas reset bar — mirrors CounterpartiesTab.
                                        Restores user-hidden / manually-stashed nodes (filter- and
                                        algorithm-stashed are reinstated by relaxing those, not here). */}
                                    {hiddenCanvasAddresses.size > 0 && (
                                        <div className="flex items-center justify-between px-2 py-1 text-[10px]">
                                            <span className="text-muted-foreground">{hiddenCanvasAddresses.size} stashed on canvas</span>
                                            <button
                                                onClick={() => {
                                                    canvasController.restoreByReason('user-hide');
                                                    canvasController.restoreByReason('manual-stash');
                                                }}
                                                className="px-2 py-0.5 text-[10px] font-medium text-primary border border-primary/30 rounded hover:bg-primary/10 transition-colors"
                                            >
                                                Show All
                                            </button>
                                        </div>
                                    )}
                                    {selectedTx && (
                                        <div className="mb-3">
                                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground/60 mb-1 font-bold">Selected Transaction</div>
                                            <TransactionCard
                                                ref={selectedTxRef}
                                                tx={selectedTx}
                                                chain={chain}
                                                onToggle={handleToggleSelect}
                                                selectedNodeId={selectedNode.id}
                                                isOnCanvas={!!(selectedTx.tx_hash && canvasTxHashes?.has(selectedTx.tx_hash))}
                                                onAddToCanvas={onAddCounterpartyToCanvas}
                                                onRemoveFromCanvas={onRemoveFromCanvas}
                                                isTrace={!!(selectedTx.tx_hash && traceTxHashes.has(selectedTx.tx_hash))}
                                                isHoverHighlighted={hoveredSource === 'canvas' && !!hoveredNodeId && (selectedTx.counterparty === hoveredNodeId || selectedTx.from_address === hoveredNodeId || selectedTx.to_address === hoveredNodeId)}
                                                onMouseEnter={() => handleRowHover(selectedTx.counterparty || null)}
                                                visibleMax={visibleMax}
                                                isDust={isDustValue(selectedTx.value, selectedTx.token_symbol, txRowUsd(selectedTx)) && !stampKind(selectedTx)}
                                            />
                                        </div>
                                    )}

                                    {traceTxs.length > 0 && (
                                        <div className="border border-amber-500/30 rounded-md bg-amber-500/5 p-1.5 space-y-0.5">
                                            <div className="text-[10px] uppercase tracking-wider text-amber-600 dark:text-amber-400 font-bold flex items-center gap-1 px-1 mb-1">
                                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block animate-pulse" />
                                                Trace Path ({traceTxs.length})
                                            </div>
                                            {traceTxs.map((tx: any, i: number) => (
                                                <TransactionCard
                                                    key={`trace-${tx.tx_hash}-${i}`}
                                                    tx={tx}
                                                    chain={chain}
                                                    onToggle={handleToggleSelect}
                                                    selectedNodeId={selectedNode.id}
                                                    isOnCanvas={!!(tx.tx_hash && canvasTxHashes?.has(tx.tx_hash))}
                                                    onAddToCanvas={onAddCounterpartyToCanvas}
                                                    onRemoveFromCanvas={onRemoveFromCanvas}
                                                    isTrace
                                                    isHoverHighlighted={hoveredSource === 'canvas' && !!hoveredNodeId && (tx.counterparty === hoveredNodeId || tx.from_address === hoveredNodeId || tx.to_address === hoveredNodeId)}
                                                    onMouseEnter={() => handleRowHover(tx.counterparty || null)}
                                                    visibleMax={visibleMax}
                                                    isDust={isDustValue(tx.value, tx.token_symbol, txRowUsd(tx)) && !stampKind(tx)}
                                                />
                                            ))}
                                        </div>
                                    )}

                                    {bigTxHashGroups.map((g) => {
                                        const expanded = expandedTxHashes.has(g.tx_hash);
                                        const allIn = g.txs.every((t: any) => (t.direction || (t.to_address === selectedNode.id ? 'incoming' : 'outgoing')) === 'incoming');
                                        const allOut = g.txs.every((t: any) => (t.direction || (t.to_address === selectedNode.id ? 'incoming' : 'outgoing')) === 'outgoing');
                                        const kindLabel = allIn ? 'co-inputs' : allOut ? 'co-outputs' : 'co-spenders';
                                        // Forensic visual identity is BLUE per §10.3 (matches the
                                        // canvas Tier-1a stroke colour). Privacy-chain visuals (above)
                                        // intentionally keep purple — that's its own taxonomy.
                                        const verifiedAny = g.txs.some((t: any) => t.forensic_context?.verified);
                                        return (
                                            <div key={`txg-${g.tx_hash}`} className={clsx(
                                                "rounded-md border",
                                                verifiedAny
                                                    ? "border-blue-500/30 bg-primary/5"
                                                    : "border-slate-500/30 bg-slate-500/5"
                                            )}>
                                                <button
                                                    type="button"
                                                    onClick={() => toggleTxHashGroup(g.tx_hash)}
                                                    className={clsx(
                                                        "w-full px-2 py-1.5 flex items-center justify-between text-[11px]",
                                                        verifiedAny ? "hover:bg-primary/10" : "hover:bg-slate-500/10"
                                                    )}
                                                    title={`tx ${g.tx_hash}`}
                                                >
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        {verifiedAny && <ShieldCheck className="w-3 h-3 shrink-0 text-primary" />}
                                                        <span className={clsx("font-semibold shrink-0", verifiedAny ? "text-primary" : "text-slate-300")}>
                                                            {g.txs.length} {kindLabel}
                                                        </span>
                                                        <span className="text-muted-foreground/70 font-mono truncate">
                                                            tx {g.tx_hash.startsWith('forensic:') ? g.tx_hash.slice(0, 24) : g.tx_hash.slice(0, 16)}…
                                                        </span>
                                                    </div>
                                                    {expanded
                                                        ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                                                        : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                                                </button>
                                                {expanded && (
                                                    <div className={clsx(
                                                        "border-t",
                                                        verifiedAny ? "border-primary/20" : "border-slate-500/20"
                                                    )}>
                                                        {g.txs.map((tx: any, i: number) => (
                                                            <TransactionCard
                                                                key={`txg-${g.tx_hash}-${i}`}
                                                                tx={tx}
                                                                chain={chain}
                                                                onToggle={handleToggleSelect}
                                                                selectedNodeId={selectedNode.id}
                                                                isOnCanvas={!!(tx.tx_hash && canvasTxHashes?.has(tx.tx_hash))}
                                                                onAddToCanvas={onAddCounterpartyToCanvas}
                                                                onRemoveFromCanvas={onRemoveFromCanvas}
                                                                isHoverHighlighted={hoveredSource === 'canvas' && !!hoveredNodeId && (tx.counterparty === hoveredNodeId || tx.from_address === hoveredNodeId || tx.to_address === hoveredNodeId)}
                                                                onMouseEnter={() => handleRowHover(tx.counterparty || null)}
                                                                visibleMax={visibleMax}
                                                                isDust={isDustValue(tx.value, tx.token_symbol, txRowUsd(tx)) && !stampKind(tx)}
                                                            />
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}

                                    {cpGroups.map((group, gi) => {
                                        // A run of 3+ transfers to the same counterparty is the
                                        // visual signal of structuring (peel chain / consolidate-
                                        // disperse). Surface it here even though the backend
                                        // pattern_detector flags the same condition — investigators
                                        // scanning the Transfers list shouldn't have to dig into
                                        // Analysis tab to spot it.
                                        const isStructuring = group.txs.length >= 3;
                                        return (
                                            <div
                                                key={`grp-${group.cpKey}-${gi}`}
                                                className={clsx(
                                                    "space-y-0.5",
                                                    // Structuring accent uses inset box-shadow + tint only —
                                                    // NEVER border-l + pl + ml. Anything that consumes
                                                    // horizontal space here would push the rows inside the
                                                    // group ~8px right of regular rows above/below it,
                                                    // breaking the alignment work shipped in Step 1c.
                                                    // Inset shadow paints over the leftmost 2px of the
                                                    // wrapper without changing the box model.
                                                    isStructuring && "bg-amber-500/5 rounded-r shadow-[inset_2px_0_0_#f59e0b99]"
                                                )}
                                            >
                                                {group.txs.length > 1 && (
                                                    <div className={clsx(
                                                        "px-2 py-0.5 flex items-center justify-between",
                                                        isStructuring
                                                            ? "text-[11px] font-semibold text-amber-700 dark:text-amber-400"
                                                            : "text-[10px] text-muted-foreground/50"
                                                    )}>
                                                        <span className="truncate flex items-center gap-1">
                                                            {isStructuring && <span title="Structuring pattern: 3+ consecutive transfers to the same counterparty">⚠</span>}
                                                            {group.cpName || truncateAddr(group.cpKey)}
                                                        </span>
                                                        <span className="tabular-nums">{group.txs.length} tx</span>
                                                    </div>
                                                )}
                                                {group.txs.map((tx: any, i: number) => (
                                                    <TransactionCard
                                                        key={`${tx.tx_hash}-${gi}-${i}`}
                                                        tx={tx}
                                                        chain={chain}
                                                        onToggle={handleToggleSelect}
                                                        selectedNodeId={selectedNode.id}
                                                        isOnCanvas={!!(tx.tx_hash && canvasTxHashes?.has(tx.tx_hash))}
                                                        onAddToCanvas={onAddCounterpartyToCanvas}
                                                        onRemoveFromCanvas={onRemoveFromCanvas}
                                                        isHoverHighlighted={hoveredSource === 'canvas' && !!hoveredNodeId && (tx.counterparty === hoveredNodeId || tx.from_address === hoveredNodeId || tx.to_address === hoveredNodeId)}
                                                        onMouseEnter={() => handleRowHover(tx.counterparty || null)}
                                                        visibleMax={visibleMax}
                                                        isDust={isDustValue(tx.value, tx.token_symbol, txRowUsd(tx)) && !stampKind(tx)}
                                                    />
                                                ))}
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })()
                    ) : (
                        <div
                            className="text-center py-8 text-muted-foreground border border-dashed border-border rounded-lg bg-secondary/10 mb-2"
                            data-testid="transfers-empty-state"
                            data-sse-sparse={isSseSparseSelection ? '1' : '0'}
                        >
                            {loadingMore ? (
                                <SkeletonList count={5} className="px-3 text-left" label="Loading transactions" />
                            ) : (
                                <>
                                    <div className="w-8 h-8 rounded-full bg-secondary/50 flex items-center justify-center mx-auto mb-2">
                                        <Clock className="w-4 h-4 text-muted-foreground" />
                                    </div>
                                    <p className="text-xs font-medium">
                                        {selectedEdgePair
                                            ? (isSseSparseSelection
                                                ? 'Edge discovered via investigation — per-tx detail not loaded'
                                                : 'Per-tx detail not loaded for this edge')
                                            : (isSseSparseSelection
                                                ? 'Node discovered via investigation — transactions not loaded'
                                                : 'No transactions loaded')}
                                    </p>
                                    <p className="text-[10px] opacity-60 mt-1 px-4">
                                        {selectedEdgePair
                                            ? 'Canvas may show an aggregated transfer; expand/load to fetch individual transactions into Transfers.'
                                            : isSseSparseSelection
                                                ? 'Run expand to load transactions from the blockchain for this address.'
                                                : 'Click below to fetch from blockchain APIs'}
                                    </p>
                                </>
                            )}
                            {(onLoadMore || onExpandNode) && (
                                <button
                                    type="button"
                                    onClick={() => { void handleEmptyStateLoad(); }}
                                    disabled={!!loadingMore}
                                    data-testid="transfers-empty-load"
                                    className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary border border-primary/30 rounded hover:bg-primary/10 disabled:opacity-50 transition-colors"
                                >
                                    {loadingMore
                                        ? 'Loading…'
                                        : selectedEdgePair ? 'Expand / load transactions' : 'Load transactions'}
                                </button>
                            )}
                        </div>
                    )}

                    {!selectedEdgePair && baseTxs.length > 0 && selectedNode?.tx_has_more === false && !selectedNode?.tx_incomplete && (
                        <p className="text-center text-[10px] text-muted-foreground mt-2 mb-4" data-testid="transfers-end-of-history">
                            End of history: all {baseTxs.length} loaded
                        </p>
                    )}
                    {onLoadMore && !dateRangeAllLoaded && !selectedEdgePair && baseTxs.length > 0
                        && !(selectedNode?.tx_has_more === false && !selectedNode?.tx_incomplete) && (
                        <>
                            {loadingMore && (
                                <SkeletonList count={3} className="mt-2" label="Loading more transactions" />
                            )}
                            <button
                            onClick={onLoadMore}
                            disabled={loadingMore}
                            className="w-full py-2 text-xs font-medium text-primary border border-primary/20 dark:border-primary/30 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-50 transition-colors mt-2 mb-4"
                        >
                            {loadingMore ? 'Loading more...' : `Load Next ${NEXT_TX_BATCH}`}
                        </button>
                        </>
                    )}
                </div>
            </TxErrorBoundary>
        </div>
    );
};

const TransactionCard = React.forwardRef<HTMLDivElement, {
    tx: any,
    chain: string | null,
    onToggle: (tx: any) => void,
    selectedNodeId: string,
    isOnCanvas?: boolean,
    onAddToCanvas?: (address: string, entityName?: string, entityType?: string) => void,
    onRemoveFromCanvas?: (address: string) => void,
    isTrace?: boolean,
    isHoverHighlighted?: boolean,
    onMouseEnter?: () => void,
    onMouseLeave?: () => void,
    /** Max abs(value) across the visible row set — used to scale the per-row volume gauge. */
    visibleMax?: number,
    /** True when the row's value is below the dust threshold — render with reduced
     *  visual weight so it doesn't compete with material rows. Set by the parent
     *  pipeline only when `hideDust === false` (otherwise dust is filtered
     *  out before render). */
    isDust?: boolean
}>(({ tx, chain, onToggle, selectedNodeId, isOnCanvas, onAddToCanvas, onRemoveFromCanvas, isTrace, isHoverHighlighted, onMouseEnter, onMouseLeave, visibleMax, isDust }, ref) => {

    const isIncoming = tx.direction ? tx.direction === 'incoming' : (tx.to_address === selectedNodeId);
    const isForensic = tx.forensic_context?.verified;

    const fromAddr = String(tx.from_address || '');
    const toAddr = String(tx.to_address || '');
    const cpAddr = (tx.counterparty as string | undefined)
        || (fromAddr.toLowerCase() === (selectedNodeId || '').toLowerCase() ? toAddr : fromAddr);
    const entityName = displayEntityName(cpAddr, tx.counterparty_entity || tx.other_address_entity_name);
    const fromEntityName = displayEntityName(fromAddr, tx.from_entity_name);
    const toEntityName = displayEntityName(toAddr, tx.to_entity_name);
    const isHighRisk = (tx.counterparty_risk ?? tx.other_address_risk_score ?? 0) > 0.7;

    const token = safeStr(tx.token_symbol) || (chain === 'TRX' ? 'TRX' : chain === 'BTC' ? 'BTC' : chain === 'SOL' ? 'SOL' : 'ETH');
    const rawValue = tx.value ?? 0;
    const valueDisplayStr = formatCurrency(rawValue, token);
    const stampText = stampBadgeText(tx);
    const unknownValue = !stampText && isUnknownValue(rawValue, token, txRowUsd(tx));
    // Use the *displayed* magnitude for the gauge so the bar reflects what the
    // user is reading (matters in USD mode where token→USD conversion changes
    // which row is largest).
    const displayMag = getDisplayMagnitude(rawValue, token);
    const gaugePct = visibleMax && visibleMax > 0
        ? Math.min(100, Math.max(2, (displayMag / visibleMax) * 100))
        : 0;

    const dateStr = tx.timestamp
        ? toSafeDate(tx.timestamp)?.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
        : '—';

    const borderColor = isTrace
        ? 'border-l-amber-500'
        : isForensic
            ? 'border-l-blue-500 shadow-[inset_0_0_10px_rgba(59,130,246,0.05)]'
            : isHighRisk
                ? 'border-l-rose-500'
                : isIncoming
                    ? 'border-l-green-500'
                    : 'border-l-red-500';

    return (
        <div
            ref={ref}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            title={isDust ? `Dust transaction (≈$0.00, below \$${DUST_USD_THRESHOLD} threshold). Hidden by default — toggle "Show dust" in the toolbar to see these.` : undefined}
            className={clsx(
                "relative overflow-hidden border-l-[3px] border-b border-border/30 last:border-b-0 px-2 py-1 transition-colors hover:bg-muted/40",
                borderColor,
                isForensic && "bg-primary/5 hover:bg-primary/10",
                isOnCanvas && !isForensic && "bg-primary/5",
                isHoverHighlighted && "ring-1 ring-inset ring-blue-500/60 bg-primary/5",
                // Dust rows: half-opacity + reduced text size + italic value.
                // Hover lifts opacity so the user can still read details.
                isDust && "opacity-45 italic hover:opacity-90 text-[12px]"
            )}
        >
            {/* Row-wide volume gauge: a translucent fill behind the content scaled
                to abs(value) / max(visible). Reads as "this row IS the bar" and is
                immune to alignment drift caused by the optional Verified badge. */}
            {gaugePct > 0 && (
                <div
                    className={clsx(
                        "absolute inset-y-0 left-0 pointer-events-none transition-[width] duration-200",
                        isForensic ? "bg-blue-400/10"
                            : isIncoming ? "bg-green-500/10"
                            : "bg-red-500/10"
                    )}
                    style={{ width: `${gaugePct}%` }}
                    aria-hidden
                    title={`${gaugePct.toFixed(0)}% of visible max`}
                />
            )}
            <div className="relative flex items-center gap-2">
                {isIncoming
                    ? <ArrowDownLeft className={clsx("w-4 h-4 shrink-0", isForensic ? "text-primary" : "text-green-500")} />
                    : <ArrowUpRight className={clsx("w-4 h-4 shrink-0", isForensic ? "text-primary" : "text-red-500")} />
                }

                <span
                    className="shrink-0 text-[10px] text-muted-foreground tabular-nums w-[70px]"
                    title="On-chain block time (confirmation timestamp), shown in your local timezone — not when the transfer was loaded into the app"
                >
                    {dateStr}
                </span>

                <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-1 min-w-0 flex-1 text-[12px] pl-2 mr-2">
                    {(() => {
                        // Pick the from-side display name with fallback chain:
                        //   counterparty entity (only when selected node is the
                        //   recipient) → tx.from_entity_name (or canvas backfill)
                        //   → truncated address.
                        const fromName = isIncoming ? (entityName || fromEntityName) : fromEntityName;
                        const fromText = fromName || truncateAddr(fromAddr);
                        // Hover title: prefer full name + full address, else
                        // just the address. Browser-native tooltip — no
                        // extra component needed.
                        const fromTitle = fromName ? `${fromName} — ${fromAddr}` : fromAddr;
                        return (
                            <EntityNameWithCopy
                                address={fromAddr}
                                title={fromTitle}
                                className={clsx("font-medium", isForensic ? "text-primary font-bold" : fromName ? 'text-primary' : 'font-mono text-foreground/80')}
                                iconClassName="w-2.5 h-2.5"
                            >
                                {fromText}
                            </EntityNameWithCopy>
                        );
                    })()}
                    <span className="text-muted-foreground/40 shrink-0 text-[10px]">→</span>
                    {(() => {
                        const toName = !isIncoming ? (entityName || toEntityName) : toEntityName;
                        const toText = toName || truncateAddr(toAddr);
                        const toTitle = toName ? `${toName} — ${toAddr}` : toAddr;
                        return (
                            <EntityNameWithCopy
                                address={toAddr}
                                title={toTitle}
                                className={clsx("font-medium", isForensic ? "text-primary font-bold" : toName ? 'text-primary' : 'font-mono text-foreground/80')}
                                iconClassName="w-2.5 h-2.5"
                            >
                                {toText}
                            </EntityNameWithCopy>
                        );
                    })()}
                </div>

                {/* Verified / swap badge slot — icon only, fixed width. Holding the
                    slot at 18px keeps row-to-row alignment consistent without
                    eating the from/to grid on the dense forensic rows where
                    every row is verified. Hover shows full provenance. */}
                <div
                    className="shrink-0 w-[18px] flex items-center justify-end"
                    title={
                        isForensic
                            ? `Verified by ${tx.forensic_context.exchange}: ${tx.forensic_context.file_name}`
                            : tx.is_stablecoin_swap
                                ? `Stablecoin swap${tx.swap_pair_hint ? `: ${tx.swap_pair_hint}` : ''}${tx.swap_protocol ? ` via ${tx.swap_protocol}` : ''}`
                                : undefined
                    }
                >
                    {isForensic && <ShieldCheck className="w-3.5 h-3.5 text-primary" aria-label="Verified subpoena evidence" />}
                    {!isForensic && tx.is_stablecoin_swap && (
                        <span className="text-[9px] font-bold text-teal-600 dark:text-teal-400" aria-label="Stablecoin swap">↔</span>
                    )}
                </div>

                {/* Value: fixed min-width + right-aligned. Different value
                    string widths ($1,799 vs $273,768) used to push the flex-1
                    grid above into different per-column widths, causing the
                    same entity name to truncate differently on each row. */}
                <span className={clsx(
                    "shrink-0 text-[12px] font-semibold tabular-nums text-right min-w-[76px]",
                    isForensic ? "text-primary" : tx.forensic_context?.verified ? 'text-primary' : isIncoming ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                )}>
                    {stampText ? (
                        <span
                            data-testid="transfer-stamp-badge"
                            className="inline-block px-1 rounded text-[10px] font-medium bg-amber-500/15 text-amber-600 dark:text-amber-400 whitespace-nowrap"
                            title={stampKind(tx) === 'counterfeit'
                                ? `Counterfeit token: it imitates another token and carries no value. Excluded from totals.${tx.counterfeit_reason ? ` Reason: ${tx.counterfeit_reason}.` : ''}`
                                : `Look-alike sender: its address imitates a counterparty this wallet paid${tx.poisoning_lookalike_of ? ` (${tx.poisoning_lookalike_of})` : ''}. Excluded from totals.`}
                        >
                            {stampText}
                        </span>
                    ) : (
                        <>
                            {valueDisplayStr}
                            {unknownValue && (
                                <span data-testid="transfer-unknown-value" className="ml-1 text-[9px] font-normal text-muted-foreground" title="No USD figure and no price for this token">unknown value</span>
                            )}
                        </>
                    )}
                </span>

                {/* Actions: fixed min-width too. The Copy-tx-hash button is
                    conditional on tx.tx_hash, so without a reservation the
                    actions column itself shrinks on rows without a hash and
                    grows the grid above by the same amount. */}
                <div className="shrink-0 flex items-center justify-end gap-0.5 ml-1.5 min-w-[48px]">
                    {(() => {
                        const cpAddr = tx.counterparty
                            || (tx.from_address && tx.from_address !== selectedNodeId ? tx.from_address : null)
                            || (tx.to_address && tx.to_address !== selectedNodeId ? tx.to_address : null);
                        if (!cpAddr) return null;
                        
                        const els = useGraphStore.getState().elements;
                        const normCpAddr = normalizeCanvasAddr(cpAddr);
                        const cpEl = els.find(el => !el.data?.source && normalizeCanvasAddr(el.data?.id) === normCpAddr);
                        const cpOnCanvas = !!cpEl;
                        // v5.3: "hidden" === stashed in useStashStore (any reason).
                        const stashEntries = useStashStore.getState().entries;
                        const stashedEntry = Object.values(stashEntries).find(e =>
                            e.group === 'nodes' && normalizeCanvasAddr(e.element?.data?.id) === normCpAddr
                        );
                        const stashedId = stashedEntry?.element?.data?.id || null;

                        if (cpOnCanvas) {
                            return (
                                <button onClick={(e) => { e.stopPropagation(); onRemoveFromCanvas?.(cpAddr); }} className="p-0.5 hover:bg-primary/20 rounded" title="Stash from canvas">
                                    <Eye className="w-3.5 h-3.5 text-primary" />
                                </button>
                            );
                        } else if (stashedId) {
                            return (
                                <button onClick={(e) => { e.stopPropagation(); canvasController.restore([stashedId]); }} className="p-0.5 hover:bg-muted rounded" title="Restore from stash">
                                    <EyeOff className="w-3.5 h-3.5 text-foreground" />
                                </button>
                            );
                        } else {
                            return (
                                <button onClick={(e) => { e.stopPropagation(); onAddToCanvas?.(cpAddr, tx.counterparty_entity || tx.other_address_entity_name, tx.counterparty_type || tx.other_address_entity_type); }} className="p-0.5 hover:bg-muted rounded" title="Add to canvas">
                                    <Plus className="w-3.5 h-3.5 text-muted-foreground/40" />
                                </button>
                            );
                        }
                    })()}
                    {tx.tx_hash && (
                        <button onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(tx.tx_hash); toast.success('Copied'); }} className="p-0.5 hover:bg-muted rounded" title={tx.tx_hash}>
                            <Copy className="w-3.5 h-3.5 text-muted-foreground" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
});
TransactionCard.displayName = 'TransactionCard';


